import { useMemo, useEffect } from 'react';
import { Channel, PlanType } from '@src/models/channel';
import { useChannelDetail } from '@src/hooks/channel/queries';
import type { ApplicationMemberResponseModel, OwnershipLevel } from '@uniquegood/realworld-admin-interface';
import { getPlanLabel, type PlanType as ConstantsPlanType } from '../constants';
import { Tag } from '@workspace/design-system/src/components';
import type { TagColor } from '@workspace/design-system/src/components/tag/tag';
import { formatPhoneNumberForDisplay } from '@src/utils/phone';
import { formatDateTime } from '@src/utils/time';

export interface ChannelInfoTabProps {
  channel: Channel;
}

export function ChannelInfoTab({ channel }: ChannelInfoTabProps) {
  // 채널 상세 정보 조회
  const { data: channelDetail, isLoading } = useChannelDetail(channel.channelId);
  
  // API에서 가져온 데이터가 있으면 사용, 없으면 props로 받은 channel 사용
  const displayChannel = channelDetail ?? channel;

  // 요금제 배지 색상 매핑
  const getPlanTagColor = (plan?: PlanType): TagColor => {
    if (!plan) return 'gray';
    const normalizedPlan = plan.toLowerCase() as ConstantsPlanType;
    if (normalizedPlan === 'free') return 'gray';
    if (normalizedPlan === 'realworld') return 'black';
    return 'green'; // pro, enterprise, teacher, class
  };

  // 요금제 배지 생성
  const getPlanBadge = (plan?: PlanType) => {
    if (!plan) {
      return (
        <Tag
          type="default"
          color="gray"
          size="S-16"
          text="없음"
        />
      );
    }
    const normalizedPlan = plan.toLowerCase() as ConstantsPlanType;
    const label = getPlanLabel(normalizedPlan);
    const color = getPlanTagColor(normalizedPlan);
    
    return (
      <Tag
        type="default"
        color={color}
        size="S-16"
        text={label}
      />
    );
  };

  // OwnershipLevel을 한글로 변환
  const getOwnershipLevelLabel = (level?: OwnershipLevel): string => {
    switch (level) {
      case 'Owner':
        return '소유자';
      case 'Developer':
        return '편집자';
      case 'Author':
        return '편집자';
      case 'Tester':
        return '테스터';
      default:
        return '-';
    }
  };

  // 채널 유저 정보 (members에서 변환)
  const channelUsers = useMemo(() => {
    if (!channelDetail?.members || channelDetail.members.length === 0) {
      return [];
    }
    
    return channelDetail.members.map((member: ApplicationMemberResponseModel) => ({
      role: getOwnershipLevelLabel(member.ownershipLevel),
      nickname: member.name || '-',
      id: member.id ? member.id.substring(0, 18).padEnd(18, '#') : '-',
      email: member.email || '-',
      phone: formatPhoneNumberForDisplay(member.phone),
    }));
  }, [channelDetail?.members]);

  return (
    <div className="flex flex-col gap-12 py-4">
      {/* 기본 정보 */}
      <section className="flex flex-col gap-6">
        <h2 className="text-base font-bold text-[#101010] leading-5">기본 정보</h2>

        <table className="w-full border-collapse border border-[#F0F0F0]">
          <tbody>
            {/* 채널명 */}
            <tr className="border-b border-[#F0F0F0]">
              <th className="w-28 bg-[#F5F5F5] px-4 py-3 text-left text-sm font-bold text-[#101010] leading-4">
                채널명
              </th>
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#101010] leading-5">
                    {displayChannel.channelName}
                  </span>
                  {getPlanBadge(displayChannel.plan)}
                </div>
              </td>
            </tr>

            {/* 채널 ID */}
            <tr className="border-b border-[#F0F0F0]">
              <th className="w-28 bg-[#F5F5F5] px-4 py-3 text-left text-sm font-bold text-[#101010] leading-4">
                채널 ID
              </th>
              <td className="px-4 py-3 text-sm text-[#101010] leading-5">
                {displayChannel.channelId}
              </td>
            </tr>

            {/* 생성일 */}
            <tr>
              <th className="w-28 bg-[#F5F5F5] px-4 py-3 text-left text-sm font-bold text-[#101010] leading-4">
                생성일
              </th>
              <td className="px-4 py-3 text-sm text-[#101010] leading-5">
                {isLoading ? '로딩 중...' : formatDateTime(displayChannel.createdAt)}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 채널 유저 정보 */}
      <section className="flex flex-col gap-6">
        <h2 className="text-base font-bold text-[#101010] leading-5">채널 유저 정보</h2>

        <table className="w-full border-collapse">
          <thead className="bg-[#F5F5F5]">
            <tr>
              <th className="w-20 p-3 text-left text-sm font-bold text-[#101010] leading-4">권한</th>
              <th className="p-3 text-left text-sm font-bold text-[#101010] leading-4">닉네임</th>
              <th className="p-3 text-left text-sm font-bold text-[#101010] leading-4">ID</th>
              <th className="p-3 text-left text-sm font-bold text-[#101010] leading-4">이메일</th>
              <th className="p-3 text-left text-sm font-bold text-[#101010] leading-4">연락처</th>
            </tr>
          </thead>
          <tbody>
            {channelUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-3 text-sm text-[#BFBFBF] text-center">
                  채널 유저 정보가 없습니다.
                </td>
              </tr>
            ) : (
              channelUsers.map((user, index) => (
                <tr key={`user-${index}-${user.id}`} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA] transition-colors">
                  <td className="w-20 p-3 text-sm text-[#101010] leading-5">{user.role}</td>
                  <td className="p-3 text-sm text-[#101010] leading-5">{user.nickname}</td>
                  <td className="p-3 text-sm text-[#101010] leading-5">{user.id}</td>
                  <td className="p-3 text-sm text-[#101010] leading-5">{user.email}</td>
                  <td className="p-3 text-sm text-[#101010] leading-5">{user.phone}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
